import React from 'react';
import { Text, TextLink, Card, Icon } from '@oneaudi/unified-web-components';
import { useI18n } from '@oneaudi/i18n-context';
import { formatDate } from './utils/date';
import InfoButton from './InfoButton';
import { useServicesContext, formatCount, Warranty, DangerousElement } from '@oneaudi/vtp-shared';
import { LocaleServiceV1 } from '@volkswagen-onehub/locale-service';
import { trackClick } from './tracking';
import { TrackingServiceV2 } from '@oneaudi/audi-tracking-service';
import styled from 'styled-components';
import { ContentService } from '@oneaudi/content-service';
import { PdpConfiguration } from '../../hooks/pdpHook';
import {
  SYS_SPACE_FIXED_100,
  SYS_SPACE_FIXED_200,
  SYS_SPACE_FIXED_300,
} from '@oneaudi/unified-web-common';

interface WarrantyComponentProps {
  pos: number;
  warranty: Warranty;
  vehicleType: 'used' | 'new';
}

const WarrantyComponent: React.FC<WarrantyComponentProps> = ({ pos, warranty, vehicleType }) => {
  const conditionsLabel = useI18n({
    id: 'nemo.ui.sc.warranties.warranty.condition',
    defaultMessage: 'Warranty Condition',
  });

  const warrantyLabel = useI18n({
    id: `nemo.ui.sc.warranties.${vehicleType}.warranty.${warranty.type}.title`,
    defaultMessage: warranty.text,
  });

  let warrantyText = useI18n({
    id: `nemo.ui.sc.warranties.${vehicleType}.warranty.${warranty.type}.text`,
    defaultMessage: '',
  });

  const warrantyDownloadlinkTextKey = useI18n({
    id: `nemo.ui.sc.warranties.warranty.download.text`,
    defaultMessage: 'Download warranty conditions for {{warranty}}',
  });
  const warrantyDownloadlinkAriaLabel = warrantyDownloadlinkTextKey?.replace(
    '{{warranty}}',
    warranty.text
  );

  const datePattern = useI18n({ id: `nemo.ui.sc.datePattern`, defaultMessage: 'yyyy-MM-dd' });

  const localeService = useServicesContext().getAdditionalService(
    'gfa:locale-service'
  ) as LocaleServiceV1;

  const audiContentService = useServicesContext().getAdditionalService(
    'audi-content-service'
  ) as ContentService;

  const contentFields: PdpConfiguration = audiContentService.getContent().fields;
  const locale = `${localeService?.language}-${localeService?.countryCode}`;

  const trackingService = useServicesContext().getAdditionalService(
    'audi-tracking-service'
  ) as TrackingServiceV2;

  Object.keys(warranty).forEach((key) => {
    const value = (warranty as any)[key];
    warrantyText = warrantyText.replace(
      `\${${key}:date}`,
      formatDate(new Date(value), datePattern)
    );

    warrantyText = warrantyText.replace(`\${${key}:number}`, formatCount(value, locale));
    warrantyText = warrantyText.replace(`\${${key}}`, String(value));
  });
  warrantyText = warrantyText.replace(/\$\{.*?\}/g, '');

  return (
    <StyledCard>
      <WarrantyHeadline data-testid="subheadline" margin={[0, 0, SYS_SPACE_FIXED_200, 0]}>
        <StyledText tag="span" variant={{ type: 'copy', order: '2', style: 'bold' }}>
          <DangerousElement html={warrantyLabel} as="span" />
        </StyledText>
        {contentFields?.warrantyInfoLayer?.[0]?.fields?.warrantyUrl &&
          warranty.type === contentFields?.warrantyInfoLayer[0].fields.warrantyType && (
            <InfoButton
              data-testid="infoButton"
              url={contentFields.warrantyInfoLayer[0].fields.warrantyUrl}
              warrantyType={warranty.type}
              pos={pos}
            />
          )}
      </WarrantyHeadline>

      {warrantyText && (
        <WarrantyText
          data-testid="warrantyUIText"
          variant={{
            order: '1',
            style: 'normal',
            type: 'copy',
          }}
          margin={[0, 0, SYS_SPACE_FIXED_300, 0]}
        >
          {warrantyText}
        </WarrantyText>
      )}
      {warranty.hrefCondition && (
        <StyledTextLink
          data-testid="button"
          size="sm"
          variant="standalone"
          href={warranty.hrefCondition}
          target="_blank"
          onClick={() => {
            trackClick(
              trackingService,
              'download',
              "warranty display - click on 'warranty conditions'",
              conditionsLabel,
              warranty.hrefCondition,
              'text link',
              warranty.type,
              pos.toString()
            );
          }}
          margin={['var(--spacing-relative-sm)', 0, 0, 0]}
          aria-label={warrantyDownloadlinkAriaLabel}
        >
          <StyledSpan>{conditionsLabel}</StyledSpan>
          <StyledIcon name="download" size="s" />
        </StyledTextLink>
      )}
    </StyledCard>
  );
};

const WarrantyHeadline = styled(Text)`
  display: grid;
  align-items: flex-start;
  grid-template-columns: 1fr auto;
  gap: ${SYS_SPACE_FIXED_100};
`;

const WarrantyText = styled(Text)`
  display: -webkit-box;
  -webkit-line-clamp: 3;
  -webkit-box-orient: vertical;
  overflow: hidden;
`;

const StyledTextLink = styled(TextLink)`
  display: inline-flex;
  align-items: center;
  margin-top: auto;
`;

const StyledText = styled(Text)`
  vertical-align: text-bottom;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
`;

const StyledCard = styled(Card)`
  height: 210px;
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
`;

const StyledIcon = styled(Icon)`
  overflow: visible;
  margin-left: ${SYS_SPACE_FIXED_100};
`;

const StyledSpan = styled.span`
  space-wrap: nowrap;
  display: -webkit-box;
  -webkit-line-clamp: 1;
  -webkit-box-orient: vertical;
  overflow: hidden;
  padding-bottom: 5px;
`;

export default WarrantyComponent;
